import React from 'react';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { ConfettiCanvas } from '../ConfettiCanvas';
import { resolveFontFamily } from '../../lib/fonts';

const _API = process.env.REACT_APP_BACKEND_URL || '';
export const cmsUrl = (u) => (!u ? '' : /^https?:\/\//.test(u) ? u : `${_API}${u}`);

// Brand palette — kept in sync with Landing.js BRAND constant.
const BRAND = {
  pink:   '#FF3B6B',
  orange: '#FF7A2E',
  teal:   '#06D6A0',
  yellow: '#FFD166',
  indigo: '#6366F1',
};
// Default 5-color rotation — same as Flash hero. Each word cycles through
// a 3-color tween via Framer Motion's `animate` color array.
const DEFAULT_WORD_COLORS = [
  [BRAND.pink,   BRAND.orange, BRAND.pink],
  [BRAND.teal,   BRAND.indigo, BRAND.teal],
  [BRAND.orange, BRAND.yellow, BRAND.orange],
  [BRAND.indigo, BRAND.pink,   BRAND.indigo],
  [BRAND.yellow, BRAND.orange, BRAND.yellow],
];

const HERO_RIBBON_FALLBACK = [
  { emoji: '🍉', label: 'DIY Fruit Bouquets' },
  { emoji: '🎨', label: 'Art & Crafts' },
  { emoji: '📿', label: 'Bracelets & Keychains' },
  { emoji: '🎂', label: 'Custom Cakes' },
  { emoji: '👕', label: 'Print Your Own Gift' },
];

/* =================================================================
 *  AnimatedHeadlineWords
 *  Renders `cms.animated_words` with per-word color rotation and the
 *  optional trailing `cms.headline_emoji` accent. Honours the CMS
 *  typography controls (font family, size, alignment, per-word color
 *  override) so admins editing the Flash hero settings also affect
 *  every other variant.
 *
 *  Props:
 *    cms         — hero CMS sub-object
 *    onDark      — when true, untinted words default to white (used by
 *                  Video / Minimalist variants)
 *    align       — overrides cms.headline_alignment ('left'|'center'|'right')
 *    className   — extra h1 classes (size, leading, weight, etc.)
 *    showEmoji   — append the rotating emoji accent (default true)
 *    testid      — data-testid on the h1 (default 'hero-headline')
 * ================================================================= */
export const AnimatedHeadlineWords = ({
  cms = {},
  onDark = false,
  align,
  className = '',
  showEmoji = true,
  testid = 'hero-headline',
}) => {
  const words = (Array.isArray(cms.animated_words) && cms.animated_words.length > 0)
    ? cms.animated_words : ['CREATE.', 'PLAY.', 'CELEBRATE.'];
  const customWordColors = Array.isArray(cms.headline_word_colors) ? cms.headline_word_colors : [];
  const wordColorAt = (idx) => {
    const c = customWordColors[idx];
    if (typeof c === 'string' && c.trim()) return [c, c, c];
    return DEFAULT_WORD_COLORS[idx % DEFAULT_WORD_COLORS.length];
  };
  const emoji = cms.headline_emoji ?? '🎉';
  const fontFamily = resolveFontFamily(cms.headline_font_family);
  const fontSizePx = Number(cms.headline_font_size_px);
  const fontSize   = fontSizePx > 0 ? `clamp(2.25rem, 6vw, ${fontSizePx}px)` : undefined;
  const alignment  = align
    || (cms.headline_alignment === 'center' || cms.headline_alignment === 'right'
        ? cms.headline_alignment : 'left');
  const alignClass = alignment === 'center' ? 'text-center'
                   : alignment === 'right'  ? 'text-right' : 'text-left';
  const baseColor  = onDark ? 'rgb(255,255,255)' : '#1E1E24';

  return (
    <motion.h1
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
      className={`${className} ${alignClass}`}
      style={{ fontFamily, color: baseColor, ...(fontSize ? { fontSize } : {}) }}
      data-testid={testid}
    >
      {words.map((word, idx) => (
        <React.Fragment key={`${word}-${idx}`}>
          <motion.span
            animate={{ color: wordColorAt(idx) }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: idx * 0.4 }}
          >{word}</motion.span>{' '}
        </React.Fragment>
      ))}
      {showEmoji && (
        <motion.span
          className="inline-block"
          animate={{ rotate: [0, 12, -8, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          aria-hidden
        >{emoji}</motion.span>
      )}
    </motion.h1>
  );
};

/* =================================================================
 *  HeroBase
 *  Shared <section> wrapper — background image overlay (or gradient),
 *  optional confetti layer, optional dark overlay for variants whose
 *  layout puts copy over a photo. Each variant just renders children.
 *
 *  Props:
 *    cms                — hero CMS sub-object
 *    confettiEnabled    — override the cms.confetti_enabled toggle
 *    fallbackBg         — variant-specific fallback (string) when the
 *                         admin hasn't set a hero.background_image
 *    darkText           — true when the variant uses light copy on a
 *                         dark photo (video/minimalist) so we apply a
 *                         readable dark overlay automatically
 *    className          — extra section classes
 * ================================================================= */
export const HeroBase = ({
  cms = {},
  fallbackBg = 'linear-gradient(135deg, #FFDEE9 0%, #FFF4D6 45%, #B5FFFC 100%)',
  confettiEnabled,
  darkText = false,
  className = '',
  testidVariant,
  children,
}) => {
  const bgImage = cms.background_image ? cmsUrl(cms.background_image) : '';
  const overlay = Math.min(1, Math.max(0, Number(cms.background_overlay_opacity ?? 0.35)));
  const gradient = (cms.gradient_start || cms.gradient_mid || cms.gradient_end)
    ? `linear-gradient(135deg, ${cms.gradient_start || '#FFDEE9'} 0%, ${cms.gradient_mid || '#FFF4D6'} 45%, ${cms.gradient_end || '#B5FFFC'} 100%)`
    : fallbackBg;
  const heroBg = bgImage
    ? `linear-gradient(rgba(0,0,0,${overlay}), rgba(0,0,0,${overlay})), url(${bgImage}) center/cover no-repeat`
    : gradient;
  const showConfetti = confettiEnabled !== undefined
    ? !!confettiEnabled
    : cms.confetti_enabled !== false;

  return (
    <section
      className={`relative overflow-hidden ${className}`}
      style={{ background: heroBg }}
      data-testid="landing-hero"
      data-variant={testidVariant}
    >
      {showConfetti && <ConfettiCanvas count={120} />}
      {darkText && !bgImage && (
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1E24]/85 via-[#1E1E24]/35 to-transparent" aria-hidden />
      )}
      {children}
    </section>
  );
};

/* =================================================================
 *  HeroCommonExtras
 *  Renders the *shared* CMS-driven extras every variant needs:
 *    - location line ("We are located inside…")
 *    - 5 experience capsule pills (with position/style toggles)
 *    - trust-badge chips under the CTAs
 *
 *  Variants invoke this inside their copy column. The component
 *  reads everything from `cms.hero` so admins edit one place and
 *  every variant updates.
 *
 *  Props:
 *    cms       — hero CMS sub-object
 *    align     — 'left' | 'center' | 'right' (overrides cms align)
 *    onDark    — set to true for variants on dark backgrounds so
 *                muted defaults flip to readable on dark
 *    pillsAt   — 'above' | 'below' — bypass the CMS position toggle
 *                for variants that need a fixed placement.
 * ================================================================= */
export const HeroCommonExtras = ({ cms = {}, align, onDark = false, pillsAt }) => {
  const sub = (cms.subheadline || '').trim();
  const locationLine = cms.location_line || '';
  const trustBadges = (Array.isArray(cms.trust_badges) && cms.trust_badges.length > 0)
    ? cms.trust_badges : [];
  const items = (Array.isArray(cms.experience_pills_items) && cms.experience_pills_items.length > 0)
    ? cms.experience_pills_items : HERO_RIBBON_FALLBACK;
  const pillsPosition = ['above_ctas', 'below_ctas', 'hidden'].includes(cms.experience_pills_position)
    ? cms.experience_pills_position : 'above_ctas';
  const pillsStyle = cms.experience_pills_style === 'text' ? 'text' : 'pills';
  // Variant can lock the pills to a fixed slot regardless of CMS toggle.
  const effectivePos = pillsAt === 'above' ? 'above_ctas'
                     : pillsAt === 'below' ? 'below_ctas'
                     : pillsPosition;

  const trustAlign = align || cms.trust_badges_alignment || 'left';
  const trustJustify = trustAlign === 'center' ? 'justify-center'
                     : trustAlign === 'right'  ? 'justify-end' : 'justify-start';
  const trustFontFamily = resolveFontFamily(cms.trust_badges_font_family);
  const trustFontSize   = Number(cms.trust_badges_font_size_px) > 0
    ? `${Number(cms.trust_badges_font_size_px)}px` : undefined;
  const trustColor      = cms.trust_badges_color
    || (onDark ? 'rgba(255,255,255,0.85)' : 'rgba(30,30,36,0.75)');

  const pillsAbove = effectivePos === 'above_ctas';
  const pillsBelow = effectivePos === 'below_ctas';

  return {
    location: locationLine ? (
      <motion.p
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className={`inline-flex items-center gap-2 text-sm font-medium rounded-full px-3.5 py-1.5 mb-5 border ${
          onDark
            ? 'bg-white/15 text-white border-white/20 backdrop-blur-sm'
            : 'bg-white/70 text-[#1E1E24] border-white backdrop-blur-sm'
        }`}
        data-testid="hero-location-line"
      >
        <MapPin className="w-3.5 h-3.5" style={{ color: '#FF3B6B' }} />
        {locationLine}
      </motion.p>
    ) : null,
    sub: sub ? (
      <p
        className={`max-w-xl mb-3 ${onDark ? 'text-white/90' : 'text-[#1E1E24]/80'}`}
        style={{
          fontFamily: resolveFontFamily(cms.subheadline_font_family),
          color: cms.subheadline_color || undefined,
          fontSize: Number(cms.subheadline_font_size_px) > 0
            ? `clamp(14px, 2.2vw, ${cms.subheadline_font_size_px}px)` : undefined,
        }}
        data-testid="hero-subtext"
      >{sub}</p>
    ) : null,
    pills: ({ when = 'above' }) => {
      const show = (when === 'above' && pillsAbove) || (when === 'below' && pillsBelow);
      if (!show) return null;
      return (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
          className="flex flex-wrap gap-x-3 gap-y-2 mb-5"
          data-testid="hero-ribbon"
          data-style={pillsStyle}
        >
          {items.slice(0, 5).map((r, i) => (
            pillsStyle === 'text' ? (
              <span key={r.id || i} className={`inline-flex items-center gap-1.5 text-sm font-medium ${onDark ? 'text-white' : 'text-[#1E1E24]'}`}>
                <span className="text-base" aria-hidden>{r.emoji}</span>
                <span className="underline-offset-4 hover:underline">{r.label}</span>
              </span>
            ) : (
              <span key={r.id || i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border shadow-sm ${
                onDark
                  ? 'bg-white/15 text-white border-white/20 backdrop-blur-sm'
                  : 'bg-white text-[#1E1E24] border-[#1E1E24]/10'
              }`}>
                <span className="text-base" aria-hidden>{r.emoji}</span>
                <span>{r.label}</span>
              </span>
            )
          ))}
        </motion.div>
      );
    },
    trustBadges: trustBadges.length > 0 ? (
      <motion.ul
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
        className={`flex flex-wrap gap-x-5 gap-y-2 mt-6 ${trustJustify}`}
        style={{ fontFamily: trustFontFamily, fontSize: trustFontSize || undefined, color: trustColor }}
        data-testid="hero-trust-badges"
      >
        {trustBadges.map((b, i) => (
          <li key={i} className="inline-flex items-center gap-1.5">
            <span aria-hidden style={{ color: '#06D6A0' }}>✓</span>
            <span>{b}</span>
          </li>
        ))}
      </motion.ul>
    ) : null,
  };
};
